import Moment from 'moment';

import config from 'src/config';

import errors from 'src/constants/errors';

import {
  api,
  events,
  events as Events,
  foxsocket as Foxsocket,
  getImageSource,
  imageSizes,
  logger,
  notification as Notification
} from 'src/helpers';
import { useModel } from 'src/helpers/modelHooks';
import { getInvalidMessage, isTooShort, validate, validateObj } from 'src/helpers/validation';

const cookiePolicyStorageKey = 'medchat_cookie_policy';

export const storagePrefix = '5|';

// run after the current sync code (so subclass fields are ready), no polyfill needed
const defer =
  typeof queueMicrotask === 'function' ? queueMicrotask : (fn) => Promise.resolve().then(fn);

function useStoreModel(options) {
  useModel(this, options);
  return this;
}

export default class BaseStore {
  id = 'base';
  persistent = false;
  saveTimeout = null;
  saveDelay = 300; // ms, debounce for persistent saves
  _savePending = false;
  loaded = false;
  events = Events;
  foxsocket = Foxsocket;
  moment = Moment;
  notification = Notification;

  defaultState = () => ({});

  state = {};
  _version = 0;
  subscriptions = [];
  requests = {};
  apiConfig = {};
  inputConfig = {};
  invalid = {};
  defaultValidateApi = '';
  _events = {};
  isLogged = false;
  cookiePolicy;
  defaultUserAvatarUri = '';

  listState = {};
  defaultListState = () => ({
    items: [],
    lastRefresh: 0
  });
  listPersistent = false;
  outdated = true;

  constructor() {
    this.imageSizes = imageSizes;
    // subclass fields are set after super() returns, so _init must wait one tick
    defer(this._init);
  }

  log() {
    console.warn('model not yet ready for use, check setup');
  }
  warn() {
    console.warn('model not yet ready for use, check setup');
  }

  _init = () => {
    this.log = logger.model.bind(logger, 'model', this.id);
    this.warn = logger.warn.bind(logger, 'model', this.id);
    this.state = this.defaultState();
    events.on('logout', this._handleLogout);
    events.asap('profile', this.setUser);
    events.asap('logged', this._handleLogged);
    Object.keys(this._events).map((event) => {
      events.asap(event, this[this._events[event]]);
    });
    events.on('cookiePolicy', this.setCookiePolicy);
    events.on('resetStates', this.handleResetStates);

    // flush debounced save when the page is hidden/closed
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('pagehide', this.flushSave);
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushSave();
      });
    }

    this.cookiePolicy = this.getCookiePolicy();

    this.trigger();
  };
  _handleLogout = () => {
    this.log('_handleLogout');
    this.isLogged = false;
    this.user = {};
    this.userId = 0;
    this.clear(true);
  };
  _handleLogged = (user) => {
    this.user = user;

    this.setDefaultAvatar(user);

    this.userId = user.id;

    this.isLogged = true;
    if (this.loaded) {
      let silent = this.onLoad();
      if (!silent) this.trigger();
      return;
    }
    if (this.persistent || this.listPersistent) {
      this.load();
    } else {
      this.loaded = true;
      this.setState(this.defaultState());
      this.onLoad();
    }
  };

  handleResetStates = () => {
    this.log('handleResetStates');
    this.state = this.defaultState();
    this.trigger();
  };

  emit(event, data) {
    events.emit(event, data);
  }
  on(event, cb) {
    events.on(event, cb);
  }
  asap(event, cb) {
    events.asap(event, cb);
  }
  cancel(event, cb) {
    events.cancel(event, cb);
  }

  use = useStoreModel;

  sid() {
    return config.get('sid');
  }
  baseSubscribe(cb, reset) {
    if (reset) this.reset();
    this.subscriptions.push(cb);
    cb(this.state);
    if (this.mounted) return;
    this.mounted = true;
    if (!this.wasMounted) {
      this.on('appState:foreground', this.onForeground);
      this.on('foxsocket:connected', this.onConnected);
      this.on('outdated', this.onOutdated);
    }
    this.wasMounted = true;
    this.onMount();
  }
  subscribe(cb, reset) {
    this.baseSubscribe(cb, reset);
  }
  unsubscribe(cb, reset) {
    let index = this.subscriptions.indexOf(cb);
    if (index !== -1) {
      this.subscriptions.splice(index, 1);
    } else {
      this.log('unsubscribe fail, cb not found');
    }
    // App-level listeners (foreground / connected / outdated) are bound once in subscribe()
    // and live as long as the store. Stores are module singletons, so nothing piles up.
    // Do NOT cancel them here: subscribe() binds only when !wasMounted, so a re-mounted
    // store would stay deaf, and List.onConnected needs them while no component is mounted.
    if (this.subscriptions.length === 0 && this.mounted) {
      this.mounted = false;
      this.onUnmount();
    }
    if (reset) this.reset();
  }

  onMount = () => {};
  onUnmount = () => {};

  onForeground = () => {};

  // hook: List overrides this to refresh after a reconnect
  onConnected = () => {};

  onOutdated = () => {
    this.outdated = true;
  };

  setStateSilent(state) {
    this.setState(state, null, true);
  }

  setState(state, cb, silent) {
    if (!state || typeof state != 'object') {
      this.warn('BaseStore.setState bad state:', state);
      return;
    }
    if (!this.loaded) this.loaded = true;
    logger.state(
      'BaseStore.setState id:',
      this.id,
      ', persistent: ',
      this.persistent,
      'state:\n',
      state
    );
    // new reference per update (no in-place mutation)
    this.state = { ...this.state, ...state };
    if (!silent) this.trigger(cb);
    if (this.persistent) this.scheduleSave();
  }
  getState() {
    return this.state || this.defaultState();
  }

  saveState(silent, cb) {
    if (!silent) this.trigger(cb);
    if (this.persistent) this.scheduleSave();
  }

  handleChange = (key, value, append = false, silent = false) => {
    if (typeof value === 'undefined') {
      value = key;
      key = 'input';
    }
    this.log('handleChange', { key, value, append, silent });
    this.cancelInvalid(key);
    let inputConfig = this.inputConfig[key];
    let validateType = inputConfig && inputConfig.validation;
    var invalid = false;

    this.setState(
      {
        [key]: !(append === true) ? value : this.input(key) + value
      },
      null,
      silent
    );

    if (inputConfig && inputConfig.submitOnChange) {
      if (validateType) {
        invalid = validate(validateType, value, {
          noMinLength: true
        });
        this.setInvalid(key, invalid);
        if (invalid) return invalid;
      }
      if (inputConfig.submitOnChange && !invalid && !isTooShort(validateType, value)) {
        // todo - add configurable timeout
        this.handleSubmit(key, value, inputConfig.submitOnChange);
      }
    }
  };
  input = (key) => {
    if (!key) key = 'input';

    return typeof this.state[key] !== 'undefined' ? this.state[key] : '';
  };

  // copy an "invalid" map (object or array-like) so we never mutate the old one
  cloneInvalid = (invalid) => {
    if (Array.isArray(invalid)) return Object.assign([], invalid);
    return { ...(invalid || {}) };
  };

  // replace the whole request entry with a new reference
  setRequest = (id, value) => {
    this.requests = { ...this.requests, [id]: value };
  };

  // merge a patch into an existing request entry (no-op if it was removed)
  updateRequest = (id, patch) => {
    const current = this.requests[id];
    if (!current) return;
    this.requests = { ...this.requests, [id]: { ...current, ...patch } };
  };

  setInvalid = (key, value) => {
    const next = this.cloneInvalid(this.invalid);
    if (value) {
      next[key] = value;
    } else {
      delete next[key];
    }
    this.invalid = next;
  };

  handleBlur = (key, value, apiId) => {
    this.log('handleBlur', { key, value });
    if (!apiId) apiId = this.defaultValidateApi;
    if (typeof apiId === 'function') apiId = apiId(key, value, apiId);
    if (apiId) {
      let invalid = this.validate(key, value, apiId);
      const current = this.getApiState(apiId);
      const nextInvalid = this.cloneInvalid(current.invalid);
      nextInvalid[key] = invalid;
      this.setRequest(apiId, { ...current, invalid: nextInvalid });
    }
    let inputConfig = this.inputConfig[key];
    if (inputConfig) {
      if (inputConfig.validation) {
        this.setInvalid(key, validate(inputConfig.validation, value));
      }
      if (inputConfig.submitOnBlur) {
        this.handleSubmit(key, value, inputConfig.submitOnBlur);
      }
    }
    this.trigger();
  };
  handleSubmit = (key, value, apiId) => {
    if (typeof value === 'string') value = value.trim();
    this.api(apiId, { value }, () => {});
  };
  validateType = (type, value) => {
    return validate(type, value);
  };
  isValid = (validateType, value) => {
    return !this.validateType(validateType, value);
  };
  validate(key, value, apiId) {
    if (!apiId) apiId = this.defaultValidateApi;
    if (!apiId) return;
    let validateConfig = this.getApiConfig(apiId).validation;
    if (!validateConfig) return;
    let validateType = validateConfig[key];
    if (!validateType) return;
    if (typeof value === 'string') value = value.trim();
    return validate(validateType, value);
  }
  trigger = (cb2) => {
    this._version += 1;
    clearTimeout(this.triggerDelayTimeout);
    delete this.triggerDelayTimeout;
    this.subscriptions.map((cb) => {
      cb(this, cb2);
    });
  };

  triggerDelay = (timeout = 0) => {
    clearTimeout(this.triggerDelayTimeout);
    this.triggerDelayTimeout = setTimeout(this.trigger, timeout);
  };

  reset(silent = false) {
    this.state = this.defaultState();
    this.listState = {};
    this.resetApi();
    if (!silent) this.trigger();
  }

  resetApi() {
    Object.keys(this.requests).map((id) => {
      this.cancelApi(id);
    });
    this.requests = {};
    this.invalid = {};
  }

  get(key) {
    return (this.state && this.state[key]) || null;
  }

  getApiConfig(id) {
    if (typeof id === 'function') id = id();
    let apiConfig = this.apiConfig[id];
    if (typeof apiConfig == 'string') {
      apiConfig = { url: apiConfig, cancellable: true, validation: {} };
    }
    if (apiConfig && apiConfig.url) {
      let config = apiConfig;
      if (!config.validation) config.validation = {};
      config.cancellable = !!config.cancellable;
      return config;
    } else {
      this.warn('getApiConfig missing config for', id);
      return {
        url: id,
        validation: {},
        cancellable: true
      };
    }
  }

  api = async (id, req, cb, silentSuccess, silentStart, onProgress) => {
    let apiConfig = this.getApiConfig(id);
    if (apiConfig.cancellable) this.cancelApi(id);
    if (typeof apiConfig.params === 'function') {
      req = apiConfig.params(req);
    }
    let invalid = validateObj(req, apiConfig.validation);
    let loading = invalid.length === 0;
    this.setRequest(id, { ...this.initialApiState(), loading, invalid });
    this.invalid = invalid;
    if (!silentStart) this.trigger();
    if (invalid.length > 0) {
      this.warn('Base api, invalid api request', id, req, invalid);
      return;
    }
    let haveFiles = (req['files'] && req['files'].length > 0) || req.haveFiles;
    if (haveFiles) {
      req['files'] = req['files'].map((file) => {
        return file.image || file.file || file;
      });
    }
    let url = apiConfig.url;
    const request = apiConfig.wsapi
      ? this.foxsocket().wsapi(url, req)
      : api(url, req, haveFiles, onProgress);
    this.updateRequest(id, { req, request });

    let { data, error } = await request;
    if (error !== 'canceled') {
      if (error) {
        this.warn('api error:', { id, url, error }, req, data);
        if (error === 'NOT_AUTHORIZED') {
          this.emit('logout');
        }
      }
      let success = error === null && data !== null;
      const result = {
        invalid: {},
        loading: false,
        success,
        error,
        errorMessage: error ? errors(error) : '',
        response: data,
        progress: 100,
        request: null
      };
      if (error && error.error === 'INVALID_REQUEST' && error.key) {
        let errorKey = error.key;
        let invalid = 'Invalid';
        let invalidKey = apiConfig.validation && apiConfig.validation[errorKey];
        if (invalidKey) {
          invalid = getInvalidMessage(invalidKey, error.value);
        }
        this.setInvalid(errorKey, invalid);
        result.invalid[errorKey] = invalid;
        result.errorMessage = '';
      }
      this.setRequest(id, result);
      if (cb) {
        typeof cb === 'function' && cb(error, data, success);
        if (success) {
          typeof cb === 'string' && this.emit(cb, data);
          typeof cb === 'boolean' && cb === true && this.emit(id, data);
        }
      }
      if (!silentSuccess) this.trigger();
    }
  };
  upload = async (id, req, progressCb, cb) => {
    let apiConfig = this.getApiConfig(id);
    if (apiConfig.cancellable) this.cancelApi(id);
    this.setRequest(id, { ...this.initialApiState(), loading: true });
    let url = apiConfig.url;
    this.setState({ loading: true });
    let file = req.file || req;
    this.log('upload', req.file, file);

    const onProgress = (e, progress) => {
      this.updateRequest(id, { progress });
      typeof progressCb === 'function' && progressCb(e, progress);
      this.trigger();
    };

    const request = api(url, req, true, onProgress);
    file.request = request;
    this.updateRequest(id, { request });
    var data, error;
    try {
      let response = await request;
      data = response.data;
      error = response.error;
    } catch (err) {
      this.log('upload request catch', err);
      error = err;
    }
    if (error !== 'canceled') {
      if (error) {
        this.log('api error:', { url, req, res: data, error });
      }
      let success = error === null && data !== null;
      this.setRequest(id, {
        invalid: {},
        loading: false,
        success,
        error,
        errorMessage: error ? errors(error) : '',
        response: data,
        progress: 100,
        request: null
      });
      typeof cb === 'function' && cb(error, data, success);
      if (success) {
        typeof cb === 'string' && this.emit(cb, data);
        typeof cb === 'boolean' && cb === true && this.emit(id, data);
      }
      this.setState({ loading: false });
    }
  };
  initialApiState() {
    return {
      loading: false,
      success: null,
      error: null,
      errorMessage: '',
      invalid: {},
      response: null,
      request: null,
      progress: 0
    };
  }
  cancelAllApi() {
    for (let id in this.requests) {
      this.cancelApi(id);
    }
    this.invalid = {};
  }
  cancelApi(id) {
    if (this.requests[id]) {
      if (this.requests[id].loading && this.requests[id].request.cancel) {
        this.requests[id].request.cancel();
      }
      this.setRequest(id, this.initialApiState());
    }
  }
  getApiState(id) {
    return this.requests[id] || this.initialApiState();
  }

  resetApiState(id) {
    this.setRequest(id, this.initialApiState());
  }

  isInvalid = (key) => {
    if (this.invalid[key]) return this.invalid[key];
    let apiId = this.inputConfig[key] && this.inputConfig[key].submitOnBlur;
    if (apiId) {
      let apiState = this.getApiState(apiId);
      return apiState.invalid[key] || apiState.invalid['value'];
    }
    return false;
  };

  getInvalid = (key) => {
    return this.isInvalid(key);
  };

  getApiInvalid = (apiId, key) => {
    let apiState = this.getApiState(apiId);

    if (!apiState) {
      return false;
    }

    return apiState.invalid[key] || apiState.invalid['value'];
  };

  isInvalidApi = (id, req) => {
    let apiConfig = this.getApiConfig(id);
    if (!(apiConfig && apiConfig.validation)) return false;
    if (typeof apiConfig.params === 'function') {
      req = apiConfig.params(req);
    }
    let invalid = validateObj(req, apiConfig.validation);
    if (invalid.length !== 0) {
      this.log('isInvalidApi invalid', id, req, invalid, apiConfig.validation);
    }
    return invalid.length === 0;
  };

  haveInvalid = () => {
    return Object.keys(this.invalid).some((key) => key !== 'length');
  };

  cancelInvalid(key) {
    if (this.invalid && Object.prototype.hasOwnProperty.call(this.invalid, key)) {
      const next = this.cloneInvalid(this.invalid);
      delete next[key];
      this.invalid = next;
    }
    let changed = false;
    const nextRequests = {};
    Object.keys(this.requests).forEach((id) => {
      const req = this.requests[id];
      if (req.invalid && Object.prototype.hasOwnProperty.call(req.invalid, key)) {
        const nextInvalid = this.cloneInvalid(req.invalid);
        delete nextInvalid[key];
        nextRequests[id] = { ...req, invalid: nextInvalid };
        changed = true;
      } else {
        nextRequests[id] = req;
      }
    });
    if (changed) this.requests = nextRequests;
  }

  loading() {
    var loading = false;
    Object.keys(this.requests).map((id) => {
      if (this.requests[id].loading === true) loading = true;
    });
    return loading;
  }
  getErrorMessage = (errorCode) => {
    var errorMessage = errors(errorCode);
    if (typeof errorMessage != 'string') {
      errorMessage = 'Error';
    }
    return errorMessage;
  };

  setDefaultAvatar = (profile) => {
    const random = profile.id % 4;
    const avatarBaseLocation = '../assets/images/avatars/users';
    const sex = profile.gender;
    this.defaultUserAvatarUri = `${avatarBaseLocation}/${sex}${
      random === 0 ? '' : `-${random}`
    }.png`;
  };

  sort(items, { key, desc }) {
    if (!Array.isArray(items)) {
      this.log('sort fail, items are not array', items);
      return items;
    }
    items = items.sort(function (a, b) {
      let keyA = a[key];
      let keyB = b[key];
      if (keyA < keyB) return desc ? 1 : -1;
      if (keyA > keyB) return desc ? -1 : 1;
      return 0;
    });
    return items;
  }

  makeUserAvatar({ id, gender = 'other' }, image, sizes) {
    const random = id % 4 || 0;

    const avatarBaseLocation = '../assets/images/avatars/users';
    const sex = gender;

    return this.makeImageUrls(
      image,
      sizes,
      `${avatarBaseLocation}/${sex}${random === 0 ? '' : `-${random}`}.png`
    );
  }

  makeImageUrls(image, sizes = ['full'], defaultImage = this.defaultUserAvatarUri) {
    let result = {
      id: image
    };
    sizes.map((size) => {
      result[size] = getImageSource(size, image, defaultImage);

      return size;
    });
    return result;
  }

  // persistent start
  scheduleSave() {
    if (!this.persistent && !this.listPersistent) return;
    this._savePending = true;
    clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(this.flushSave, this.saveDelay);
  }

  flushSave = () => {
    if (!this._savePending) return;
    this.save();
  };

  cancelSave() {
    clearTimeout(this.saveTimeout);
    this.saveTimeout = null;
    this._savePending = false;
  }

  save() {
    if (!this.persistent && !this.listPersistent) return false;
    this.cancelSave(); // a direct save supersedes any pending one
    try {
      let data = {
        state: this.persistent ? this.state : {},
        listState: this.listPersistent ? this.listState : {}
      };
      logger.state('BaseStore.save', this.id, data);

      localStorage.setItem(storagePrefix + this.id, JSON.stringify(data));
    } catch (err) {
      logger.warn('save err', err);
    }
  }

  async load() {
    this.loaded = true;
    var data = null;

    if (this.persistent || this.listPersistent) {
      try {
        let item = await localStorage.getItem(storagePrefix + this.id);
        if (item && typeof item == 'string') data = JSON.parse(item);
      } catch (err) {
        logger.warn('load err:', err);
      }
    }

    if (data && typeof data === 'object' && data.state && data.listState) {
      if (this.persistent) {
        this.state = data.state;
      } else {
        this.state = this.defaultState();
      }
      this.listState = data.listState;
    } else {
      this.state = this.defaultState();
      this.listState = {};
    }
    let silent = this.onLoad();
    if (!silent) this.trigger();
  }
  onLoad() {
    return false;
  }

  async clear(silent) {
    this.cancelSave(); // don't let a pending save re-write cleared data
    this.state = this.defaultState();
    this.listState = {};
    this.requests = {};
    if (!silent) this.trigger();
    try {
      logger.state('BaseStore.clear', this.id, this.state);
      await this.clearLocalStorage();
      return true;
    } catch (err) {
      logger.warn('clear err:', err);
    }
    return false;
  }

  async clearLocalStorage() {
    const keys = [];

    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i).startsWith(storagePrefix)) {
        keys.push(localStorage.key(i));
      }
    }

    // Iterate over arr and remove the items by key
    for (let i = 0; i < keys.length; i++) {
      await localStorage.removeItem(keys[i]);
    }
  }

  openModal = (data) => {
    this.emit('openModal', data);
  };

  changeTab = (key, value) => {
    this.setState({
      tabs: {
        ...this.state.tabs,
        [key]: value
      }
    });
    this.emit('changeTab', { key, value });
  };

  getTab(key) {
    const tabs = this.state.tabs;
    return tabs[key];
  }

  closeModal = (forceClose) => {
    this.emit('closeModal', forceClose);
  };

  openNotifications = () => {
    this.emit('openNotifications');
  };

  openTopics = () => {
    this.emit('openTopics');
  };

  setUser = (user) => {
    this.user = user;
    if (user?.id) this.userId = user?.id;

    this.setDefaultAvatar(user);
  };

  getUser = () => {
    return this.user || this.state.user;
  };

  getCookiePolicy = async () => {
    return localStorage.getItem(cookiePolicyStorageKey);
  };

  setCookiePolicy = async (value) => {
    this.cookiePolicy = value;
    await localStorage.setItem(cookiePolicyStorageKey, value);

    this.trigger();
  };

  handleChangeProfileAndSwitchAccount = (profile) => {
    let reset = false;
    if (!this.state.user || profile?.id !== this.state.user?.id) {
      reset = true;
    }

    this.user = profile;
    this.setState({ user: profile, ...(this.cacheState || {}) });
    this.onHandleChangeProfile(profile);

    if (reset) {
      this.log('accountSwitched, reset states');
      this.onLoad();
    }
  };

  onHandleChangeProfile = () => {};

  setInvalidApi = (api, invalid, silent = false) => {
    this.setRequest(api, { ...this.initialApiState(), invalid });
    this.invalid = invalid;
    if (!silent) this.trigger();
  };
}
